/**
 * Audit-Trail CSV-Serialisierung (Spec 530, Phase 1 — reine Logik).
 *
 * Erzeugt aus einer Event-Liste + Export-Metadaten einen CSV-Text als
 * regulatorischen Pruef-Beleg: `#`-markierter Bericht-Kopf (Dimension/Zeitraum,
 * Filter, Erstellzeitpunkt, Anzahl, ggf. Deckel-Offenlegung), Leerzeile,
 * Spaltenkopf, flache Datenzeilen. RFC-4180-Quoting, CRLF, UTF-8-BOM (Excel),
 * Geheimnisse nur als Redaktions-Marker (nie im Klartext), Formel-Injection
 * neutralisiert. Rein (kein React/DOM/Netzwerk) → isoliert testbar.
 */

import type { AuditEvent, AuditFieldValue } from '@/types/audit';

/**
 * Lokalisierte Feld-Labels fuer den `#`-Bericht-Kopf. Der Aufrufer (mit `t()`)
 * liefert sie, damit die reine Serialisierungsfunktion keinen sichtbaren Text
 * hartkodiert (Spec 530: alle sichtbaren Strings de + en).
 */
export interface AuditCsvLabels {
  title: string;
  dimension: string;
  filter: string;
  generatedAt: string;
  events: string;
  /** Voll formatierter Hinweis (inkl. Deckel-Wert), nur bei `truncated` genutzt. */
  truncatedNote: string;
}

/** Metadaten fuer den Bericht-Kopf des Exports. */
export interface AuditCsvMeta {
  /**
   * Menschenlesbare Abfrage-Dimension inkl. Zeitraum, z.B. "Tag: 2026-08-04"
   * oder "Zeitfenster: 2026-08-01 bis 2026-08-04". Der Phase-5-Consumer faltet
   * Dimension und Zeitraum bewusst in diesen einen String (kein separates
   * `rangeLabel`).
   */
  dimensionLabel: string;
  /** Aktive Zusatzfilter als vorformatierter, komma-freier String (optional). */
  filters?: string;
  /** Erstellzeitpunkt (ISO-8601 UTC). */
  generatedAt: string;
  /** Anzahl exportierter Events. */
  count: number;
  /** True, wenn der Sicherheits-Deckel erreicht wurde (Export unvollstaendig). */
  truncated: boolean;
  /** Lokalisierte Kopf-Labels (vom Aufrufer). */
  labels: AuditCsvLabels;
}

/** Feste Spaltenreihenfolge des CSV (flach, pruefer-tauglich). */
export const AUDIT_CSV_COLUMNS = [
  'timestamp',
  'eventId',
  'tier',
  'result',
  'actorEmail',
  'actorRole',
  'actorId',
  'action',
  'resourceType',
  'resourceId',
  'merchantId',
  'impactScope',
  'sourceIp',
  'requestId',
  'changedFields',
  'previousValue',
  'newValue',
] as const;

const BOM = '﻿';
const CRLF = '\r\n';

/** Redaktions-Marker erkennen (Geheimnis — nie der Wert). */
function isRedacted(v: unknown): v is { redacted: true; changeKind?: string } {
  return typeof v === 'object' && v !== null && (v as { redacted?: boolean }).redacted === true;
}

/**
 * Serialisiert einen Vorher/Nachher-Wert fuer eine CSV-Zelle:
 * Redaktions-Marker → `redacted:<changeKind>`; Objekt → kompaktes JSON;
 * null → "null"; undefined → leer; sonst der String-Wert.
 */
export function serializeAuditValue(v: AuditFieldValue | undefined): string {
  if (v === undefined) return '';
  if (v === null) return 'null';
  if (isRedacted(v)) return `redacted:${v.changeKind ?? ''}`;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * RFC-4180-CSV-Feld: Formel-Injection (`= + - @` fuehrend) neutralisieren, dann
 * bei `,` `"` CR oder LF in Anfuehrungszeichen setzen und interne `"` verdoppeln.
 */
export function csvField(raw: string): string {
  let value = raw;
  if (/^[=+\-@]/.test(value)) value = `'${value}`;
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** Baut eine flache Datenzeile aus einem Event (feste Spaltenreihenfolge). */
function eventToRow(ev: AuditEvent): string {
  return AUDIT_CSV_COLUMNS.map((col) => {
    switch (col) {
      case 'changedFields':
        return csvField((ev.changedFields ?? []).join('|'));
      case 'previousValue':
        return csvField(serializeAuditValue(ev.previousValue));
      case 'newValue':
        return csvField(serializeAuditValue(ev.newValue));
      case 'tier':
        return csvField(String(ev.tier));
      default: {
        const value = ev[col];
        return csvField(value === null || value === undefined ? '' : String(value));
      }
    }
  }).join(',');
}

/**
 * Serialisiert Events + Metadaten zu einem vollstaendigen CSV-Text (mit BOM).
 * Aufbau: `#`-Kopfblock → Leerzeile → Spaltenkopf → Datenzeilen.
 */
export function serializeAuditCsv(events: AuditEvent[], meta: AuditCsvMeta): string {
  const { labels } = meta;
  const headerLines = [
    `# ${labels.title}`,
    `# ${labels.dimension}: ${meta.dimensionLabel}`,
    ...(meta.filters ? [`# ${labels.filter}: ${meta.filters}`] : []),
    `# ${labels.generatedAt}: ${meta.generatedAt}`,
    `# ${labels.events}: ${meta.count}`,
    ...(meta.truncated ? [`# ${labels.truncatedNote}`] : []),
  ];

  const lines = [
    ...headerLines,
    '',
    AUDIT_CSV_COLUMNS.join(','),
    ...events.map(eventToRow),
  ];

  return BOM + lines.join(CRLF) + CRLF;
}
